import React, { useEffect } from "react";
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import SidebarNav from "@/components/dashboard/SidebarNav";
import AppShellFrame from "@/components/layout/AppShellFrame";
import ShellTopBar from "@/components/layout/ShellTopBar";
import EmployeeSearchBox from "@/components/ui/EmployeeSearchBox";
import WorkflowPager from "@/components/ui/WorkflowPager";
import { useAuth } from "@/contexts/AuthContext";
import { ShellEmployeeSelectionProvider } from "@/contexts/ShellEmployeeSelectionContext";
import { buildVisibleCompanyShellNavigation } from "@/data/companyShellNavigation";
import {
  ShellEmployeeSearchProvider,
  useShellEmployeeSearch,
} from "@/contexts/ShellEmployeeSearchContext";
import { useCompanyShellCapabilities } from "@/hooks/useCompanyShellCapabilities";
import { resolveShellSearchContext } from "@/services/shellSearchContext";

type ThemeMode = "light" | "dark" | "auto";

function computeAutoIsDark(now: Date) {
  const h = now.getHours();
  return h >= 19 || h < 7;
}

function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;
  const isDark = mode === "dark" || (mode === "auto" && computeAutoIsDark(new Date()));
  root.classList.toggle("dark", isDark);
}

function ShellLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-vault border-t-transparent" />
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">
          Loading workspace...
        </p>
      </div>
    </div>
  );
}

function ShellMessage({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-xl items-center justify-center">
      <div className="w-full rounded-3xl border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-800 dark:bg-slate-950">
        <p className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-400">
          Workspace
        </p>
        <h2 className="mt-2 text-2xl font-black text-slate-900 dark:text-slate-100">
          {title}
        </h2>
        <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">{body}</p>
        {action ? <div className="mt-6">{action}</div> : null}
      </div>
    </div>
  );
}

function ShellEmptyWorkspace() {
  return (
    <ShellMessage
      title="Workspace ready"
      body="Your tools will appear here when the owner assigns you credentials."
    />
  );
}

function ShellTopBarSearch({
  enabled,
  label,
  placeholder,
}: {
  enabled: boolean;
  label: string;
  placeholder: string;
}) {
  if (!enabled) {
    return null;
  }

  const shellEmployeeSearch =
    useShellEmployeeSearch();

  return (
    <EmployeeSearchBox
      compact
      query={shellEmployeeSearch.query}
      setQuery={shellEmployeeSearch.setQuery}
      options={shellEmployeeSearch.filteredOptions}
      loading={shellEmployeeSearch.loading}
      error={shellEmployeeSearch.error}
      label={label}
      placeholder={placeholder}
      submittedQuery={
        shellEmployeeSearch.submittedQuery
      }
      hasActiveQuery={
        shellEmployeeSearch.hasActiveQuery
      }
      resultCount={
        shellEmployeeSearch.resultCount
      }
      onSubmit={
        shellEmployeeSearch.submitSearch
      }
      onClear={
        shellEmployeeSearch.clearSearch
      }
    />
  );
}

export default function CompanyShellLayout() {
  const { companyId } = useParams<{ companyId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { initializing, isAuthenticated, signOut } = useAuth();
  const { loading, error, data } = useCompanyShellCapabilities(companyId);
  const shellSearchContext = resolveShellSearchContext(location.pathname);
  
  useEffect(() => {
    const saved = (localStorage.getItem("pcu_theme") as ThemeMode | null) ?? "auto";
    const themeMode =
      saved === "light" || saved === "dark" || saved === "auto" ? saved : "auto";

    applyTheme(themeMode);

    if (themeMode !== "auto") return;

    const id = window.setInterval(() => applyTheme("auto"), 60_000);
    return () => window.clearInterval(id);
  }, []);

  async function onLogout() {
    await signOut();
    navigate("/login", { replace: true });
  }

  if (!initializing && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!companyId) {
    return (
      <ShellMessage
        title="Missing company context"
        body="The workspace could not determine which company to load."
        action={
          <Link
            to="/owner/signup"
            className="inline-flex items-center justify-center rounded-xl bg-brand-vault px-4 py-2 text-xs font-black uppercase tracking-wider text-white transition hover:opacity-90"
          >
            Return to setup
          </Link>
        }
      />
    );
  }

  if (loading || initializing) {
    return <ShellLoader />;
  }

  if (error || !data || !data.can_access_company_shell) {
    return (
      <ShellMessage
        title="Workspace access unavailable"
        body={error ?? "You do not have access to this company workspace."}
        action={
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-700 transition hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
          >
            Return to login
          </Link>
        }
      />
    );
  }

  const companyRoot = `/owner/company/${companyId}`;
  const employeeWorkspaceRoot = `${companyRoot}/payroll/my`;
  const employeePayslipsRoot = `${companyRoot}/payroll/my/payslips`;
  const employeeTimesheetsRoot = `${companyRoot}/payroll/my/timesheets`;
  const employeeDisputesRoot = `${companyRoot}/payroll/my/disputes`;
  const employeeAbsencesRoot = `${companyRoot}/payroll/my/absences`;
  const employeeLeaveRoot = `${companyRoot}/payroll/my/leave`;
  const companyRulesRoot = `${companyRoot}/company-rules`;
  const payrollRoot = `${companyRoot}/payroll`;
  const payrollPeriodsNewRoot = `${companyRoot}/payroll/periods/new`;
  const capabilityData = data;

  const pathname = location.pathname;

  const isCompanyOverviewRoute = pathname === companyRoot;
  const isSettingsRoute = pathname === `${companyRoot}/settings`;
  const isInviteEmployeeRoute = pathname === `${companyRoot}/invite-employee`;
  const isManageEmployeesRoute = pathname === `${companyRoot}/employees`;
  const isCompanyRulesRoute = pathname === companyRulesRoot;

  const isEmployeeWorkspaceRoute = pathname === employeeWorkspaceRoot;
  const isEmployeePayslipsRoute = pathname === employeePayslipsRoot;
  const isEmployeeTimesheetsRoute = pathname === employeeTimesheetsRoot;
  const isEmployeeDisputesRoute = pathname === employeeDisputesRoot;
  const isEmployeeAbsencesRoute = pathname === employeeAbsencesRoot;
  const isEmployeeLeaveRoute = pathname === employeeLeaveRoot;

  const isPayrollHomeRoute = pathname === payrollRoot;
  const isTimesheetsRoute = pathname === `${companyRoot}/payroll/timesheets`;
  const isPreflightRoute = pathname === `${companyRoot}/payroll/preflight`;
  const isSubmitRoute = pathname === `${companyRoot}/payroll/submit`;
  const isIssuerQueueRoute = pathname === `${companyRoot}/payroll/issuer`;
  const isIssuerRunRoute =
    pathname.startsWith(`${companyRoot}/payroll/run/`) &&
    (pathname.endsWith("/review") || pathname.endsWith("/issue"));
  const isPayrollRepairRoute =
    pathname.startsWith(`${companyRoot}/payroll/run/`) &&
    pathname.includes("/repair/");
  const isIssuedRunRoute = pathname.startsWith(`${companyRoot}/payroll/issued/`);
  const isPayrollPeriodsNewRoute = pathname === payrollPeriodsNewRoot;
  const isPayrollPeriodDetailRoute =
    pathname.startsWith(`${companyRoot}/payroll/periods/`) &&
    pathname !== payrollPeriodsNewRoot;

  function getFirstAuthorizedRoute(): string | null {
    if (capabilityData.can_view_my_workspace) return employeeWorkspaceRoot;
    if (capabilityData.can_view_my_payslips) return employeePayslipsRoot;
    if (capabilityData.can_view_my_timesheets) return employeeTimesheetsRoot;
    if (
      capabilityData.can_view_my_disputes ||
      capabilityData.can_submit_my_disputes
    ) {
      return employeeDisputesRoot;
    }
    if (capabilityData.can_report_my_absences) return employeeAbsencesRoot;
    if (capabilityData.can_request_my_leave) return employeeLeaveRoot;
    if (capabilityData.can_view_company_overview) return companyRoot;
    if (capabilityData.can_manage_company_settings) {
      return `${companyRoot}/settings`;
    }
    if (capabilityData.can_manage_company_rule_proposals) {
      return companyRulesRoot;
    }
    if (
      capabilityData.can_submit_payroll ||
      capabilityData.can_issue_payroll ||
      capabilityData.can_manage_payroll_periods
    ) {
      return payrollRoot;
    }
    return null;
  }

  let redirectTo: string | null = null;

  if (
    isCompanyOverviewRoute &&
    !capabilityData.can_view_company_overview
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    isSettingsRoute &&
    !capabilityData.can_manage_company_settings
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    isInviteEmployeeRoute &&
    !capabilityData.can_invite_employees
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    isManageEmployeesRoute &&
    !capabilityData.can_manage_employees
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    isCompanyRulesRoute &&
    !capabilityData.can_manage_company_rule_proposals
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    isEmployeeWorkspaceRoute &&
    !capabilityData.can_view_my_workspace
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    isEmployeePayslipsRoute &&
    !capabilityData.can_view_my_payslips
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    isEmployeeTimesheetsRoute &&
    !capabilityData.can_view_my_timesheets
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    isEmployeeDisputesRoute &&
    !(
      capabilityData.can_view_my_disputes ||
      capabilityData.can_submit_my_disputes
    )
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    isEmployeeAbsencesRoute &&
    !capabilityData.can_report_my_absences
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    isEmployeeLeaveRoute &&
    !capabilityData.can_request_my_leave
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    isPayrollHomeRoute &&
    !capabilityData.can_submit_payroll &&
    !capabilityData.can_issue_payroll &&
    !capabilityData.can_manage_payroll_periods
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    (isTimesheetsRoute || isPreflightRoute || isSubmitRoute) &&
    !capabilityData.can_submit_payroll
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    isPayrollPeriodsNewRoute &&
    !capabilityData.can_manage_payroll_periods
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    isPayrollPeriodDetailRoute &&
    !capabilityData.can_submit_payroll &&
    !capabilityData.can_issue_payroll &&
    !capabilityData.can_manage_payroll_periods
  ) {
    redirectTo = getFirstAuthorizedRoute();
  } else if (
    (isIssuerQueueRoute || isIssuerRunRoute || isIssuedRunRoute) &&
    !capabilityData.can_issue_payroll
  ) {
    redirectTo = getFirstAuthorizedRoute();
  }

  if (redirectTo && redirectTo !== pathname) {
    return <Navigate to={redirectTo} replace />;
  }

  const visibleGroups = buildVisibleCompanyShellNavigation(
    companyId,
    capabilityData
  );

  const homeTo = getFirstAuthorizedRoute() ?? employeeWorkspaceRoot;

  return (
    <ShellEmployeeSearchProvider
      companyId={
        shellSearchContext.enabled
          ? companyId
          : null
      }
    >
      <ShellEmployeeSelectionProvider>
        <AppShellFrame
        sidebar={
          <SidebarNav
            homeTo={homeTo}
            companyName={capabilityData.company_name}
            groups={visibleGroups}
          />
        }
        topBar={
          <ShellTopBar
            onLogout={onLogout}
            searchSlot={
              <ShellTopBarSearch
                enabled={
                  shellSearchContext.enabled
                }
                label={shellSearchContext.label}
                placeholder={
                  shellSearchContext.placeholder
                }
              />
            }
          />
        }
      >
        {visibleGroups.length === 0 && !isPayrollRepairRoute ? (
          <ShellEmptyWorkspace />
        ) : (
          <>
            <WorkflowPager />
            <Outlet />
          </>
        )}
          </AppShellFrame>
        </ShellEmployeeSelectionProvider>
      </ShellEmployeeSearchProvider>
  );
}
